import React, { useState, useEffect, useMemo, useRef } from 'react';
import { ALL_POKEMON, ITEMS_INFO } from '../data/teams';
import { ALL_MOVES } from '../data/selectionAdvisor';
import ABILITIES, { ALL_ABILITIES } from '../data/abilities';
import { getBaseStats, getMegaStats, isMegaStone, ALL_ITEMS, canMegaEvolve, getMegaTypes, getMegaStatsByStone, getMegaStones, getMegaAbility } from '../data/baseStats';
import { TypeBadge, TYPE_COLORS } from './TeamBrowser';
import { getDefensiveProfile } from '../data/typeChart';
import { getSpriteUrl, getMegaSpriteUrl, getItemSpriteUrl, getTypeIconUrl, handleItemIconError } from '../data/sprites';
import MOVE_TYPES from '../data/moves';
import MOVE_DATA from '../data/moveData';
import {
  getLegalMoves,
  getAllLegalAbilities,
  isHiddenAbility,
  getMoveUsage,
} from '../data/pokemonMeta';
import { loadHistory, getTeamRecord, computeOptimalTeam } from '../data/matchHistory';
import { calcStat, getNatureMod } from '../data/damageCalc';
import { useAuth } from '../auth/AuthContext';
import { useTeams } from '../auth/useTeams';
import AuthModal from './AuthModal';
import { track } from '../analytics';
import StrategyEditor from './StrategyEditor';
import { emptyStrategy, normalizeStrategy } from '../data/teamStrategy';

// Status moves have types even though MOVE_TYPES returns null for them
const STATUS_MOVE_TYPES = {
  'Swords Dance': 'Normal', 'Nasty Plot': 'Dark', 'Dragon Dance': 'Dragon',
  'Calm Mind': 'Psychic', 'Quiver Dance': 'Bug', 'Shell Smash': 'Normal',
  'Bulk Up': 'Fighting', 'Coil': 'Poison', 'Curse': 'Ghost',
  'Iron Defense': 'Steel', 'Agility': 'Psychic', 'Tidy Up': 'Normal',
  'Acupressure': 'Normal', 'Amnesia': 'Psychic',
  'Protect': 'Normal', "King's Shield": 'Steel', 'Spiky Shield': 'Grass',
  'Baneful Bunker': 'Poison', 'Detect': 'Fighting',
  'Tailwind': 'Flying', 'Trick Room': 'Psychic',
  'Follow Me': 'Normal', 'Rage Powder': 'Bug', 'Helping Hand': 'Normal',
  'Ally Switch': 'Psychic', 'After You': 'Normal', 'Coaching': 'Fighting',
  'Life Dew': 'Water', 'Instruct': 'Psychic',
  'Will-O-Wisp': 'Fire', 'Thunder Wave': 'Electric', 'Toxic': 'Poison',
  'Sleep Powder': 'Grass', 'Spore': 'Grass', 'Yawn': 'Normal',
  'Encore': 'Normal', 'Taunt': 'Dark', 'Disable': 'Normal',
  'Charm': 'Fairy', 'Baby-Doll Eyes': 'Fairy', 'Scary Face': 'Normal',
  'Parting Shot': 'Dark',
  'Recover': 'Normal', 'Roost': 'Flying', 'Slack Off': 'Normal',
  'Rest': 'Psychic', 'Leech Seed': 'Grass', 'Synthesis': 'Grass',
  'Stealth Rock': 'Rock', 'Aurora Veil': 'Ice', 'Light Screen': 'Psychic',
  'Reflect': 'Psychic', 'Trick': 'Psychic', 'Switcheroo': 'Dark',
  'Aromatherapy': 'Grass', 'Baton Pass': 'Normal', 'Haze': 'Ice',
  'Whirlwind': 'Normal', 'Roar': 'Normal', 'Substitute': 'Normal',
};

const MOVE_DESCRIPTIONS = {
  // Stat boosting
  'Swords Dance': '+2 Attack',
  'Nasty Plot': '+2 Sp. Atk',
  'Dragon Dance': '+1 Attack, +1 Speed',
  'Calm Mind': '+1 Sp. Atk, +1 Sp. Def',
  'Quiver Dance': '+1 Sp. Atk, +1 Sp. Def, +1 Speed',
  'Shell Smash': '+2 Atk, +2 SpA, +2 Spe, −1 Def, −1 SpD',
  'Bulk Up': '+1 Attack, +1 Defense',
  'Coil': '+1 Attack, +1 Defense, +1 Accuracy',
  'Curse': '+1 Atk, +1 Def, −1 Speed (non-Ghost)',
  'Iron Defense': '+2 Defense',
  'Agility': '+2 Speed',
  'Tidy Up': '+1 Attack, +1 Speed, clears hazards',
  'Acupressure': '+2 to a random stat',
  // Protection
  'Protect': 'Blocks all moves this turn. Fails if used consecutively.',
  "King's Shield": 'Blocks moves, −1 Atk to contact attackers. Shield Forme.',
  'Spiky Shield': 'Blocks moves, damages contact attackers.',
  'Baneful Bunker': 'Blocks moves, poisons contact attackers.',
  'Detect': 'Same as Protect.',
  // Speed control
  'Tailwind': 'Doubles team Speed for 4 turns.',
  'Trick Room': 'Slower Pokemon move first for 5 turns.',
  'Icy Wind': 'Hits both foes, −1 Speed each.',
  'Electroweb': 'Hits both foes, −1 Speed each.',
  // Support
  'Fake Out': 'Priority +3. Flinches target. Only works turn 1.',
  'Follow Me': 'Redirects all single-target moves to user.',
  'Rage Powder': 'Redirects all single-target moves to user.',
  'Helping Hand': '+50% damage to partner\'s next move.',
  'Ally Switch': 'Swaps position with partner.',
  'After You': 'Target moves immediately after user.',
  'Coaching': '+1 Atk, +1 Def to partner.',
  'Life Dew': 'Heals user and partner by 25% HP.',
  'Instruct': 'Target repeats its last move.',
  // Status moves
  'Will-O-Wisp': 'Burns target. −50% physical damage.',
  'Thunder Wave': 'Paralyzes target. −50% Speed.',
  'Toxic': 'Badly poisons target. Damage increases each turn.',
  'Sleep Powder': 'Puts target to sleep.',
  'Spore': 'Puts target to sleep. 100% accuracy.',
  'Yawn': 'Target falls asleep next turn.',
  'Encore': 'Locks target into its last move for 3 turns.',
  'Taunt': 'Target can only use attacking moves for 3 turns.',
  'Disable': 'Disables target\'s last used move.',
  'Charm': '−2 Attack to target.',
  'Baby-Doll Eyes': 'Priority. −1 Attack to target.',
  'Scary Face': '−2 Speed to target.',
  'Parting Shot': '−1 Atk, −1 SpA to target, then switch out.',
  'U-turn': 'Attacks then switches out.',
  'Volt Switch': 'Attacks then switches out.',
  // Recovery
  'Recover': 'Heals 50% max HP.',
  'Roost': 'Heals 50% max HP. Loses Flying type this turn.',
  'Slack Off': 'Heals 50% max HP.',
  'Rest': 'Heals fully but sleeps for 2 turns.',
  'Leech Seed': 'Drains 1/8 HP each turn.',
  'Synthesis': 'Heals HP. Amount varies with weather.',
  // Hazards & utility
  'Stealth Rock': 'Damages foes on switch-in based on type.',
  'Aurora Veil': 'Halves physical & special damage for 5 turns. Requires Snow.',
  'Light Screen': 'Halves special damage for 5 turns.',
  'Reflect': 'Halves physical damage for 5 turns.',
  'Trick': 'Swaps items with target.',
  'Switcheroo': 'Swaps items with target.',
  'Knock Off': 'Removes target\'s item. 1.5x power if item removed.',
  'Amnesia': '+2 Sp. Def',
  'Aromatherapy': 'Cures all status conditions on your team.',
  'Baton Pass': 'Switches out, passing stat changes to replacement.',
  'Haze': 'Resets all stat changes on the field.',
  'Whirlwind': 'Forces target to switch out.',
  'Roar': 'Forces target to switch out.',
  'Substitute': 'Creates a decoy using 25% HP.',
};

// Darkened type colors for readable text on light backgrounds
const TYPE_TEXT_COLORS = {
  Normal: '#6d6d4e', Fire: '#c23616', Water: '#2e6cb5', Electric: '#a68a00',
  Grass: '#4a7a2e', Ice: '#4a8f8f', Fighting: '#8a2020', Poison: '#723090',
  Ground: '#8a7530', Flying: '#6a58c0', Psychic: '#c93868', Bug: '#6d7a10',
  Rock: '#8a7a28', Ghost: '#4e3870', Dragon: '#5028c8', Dark: '#504038',
  Steel: '#7a7a98', Fairy: '#c46878',
};

function moveTypeStyle(moveName) {
  const type = MOVE_TYPES[moveName] || STATUS_MOVE_TYPES[moveName];
  if (!type) return {};
  const bgColor = TYPE_COLORS[type] || '#888';
  const textColor = TYPE_TEXT_COLORS[type] || bgColor;
  return { background: bgColor + '18', color: textColor, borderColor: bgColor + '30' };
}

function PokemonSearch({ onSelect, exclude }) {
  const [query, setQuery] = useState('');
  const [focused, setFocused] = useState(false);
  const inputRef = useRef();

  const results = query.length >= 1
    ? ALL_POKEMON.filter(p =>
        p.name.toLowerCase().includes(query.toLowerCase()) &&
        !exclude.some(e => e.name === p.name)
      ).slice(0, 8)
    : [];

  const handleSelect = (p) => {
    const stats = getBaseStats(p.name);
    onSelect({
      ...p,
      moves: [],
      ability: null,
      item: null,
      nature: 'Adamant',
      statPoints: { hp: 0, atk: 0, def: 0, spa: 0, spd: 0, spe: 0 },
      baseStats: stats || null,
    });
    setQuery('');
    inputRef.current?.focus();
  };

  return (
    <div className="pokemon-search">
      <input
        ref={inputRef}
        type="text"
        placeholder="Type a Pokemon name..."
        value={query}
        onChange={e => setQuery(e.target.value)}
        onFocus={() => setFocused(true)}
        onBlur={() => setTimeout(() => setFocused(false), 150)}
      />
      {focused && results.length > 0 && (
        <div className="search-results">
          {results.map(p => {
            const stats = getBaseStats(p.name);
            const statList = stats ? [
              { key: 'hp', label: 'HP', color: '#ef4444' },
              { key: 'atk', label: 'Atk', color: '#f59e0b' },
              { key: 'def', label: 'Def', color: '#f97316' },
              { key: 'spa', label: 'SpA', color: '#3b82f6' },
              { key: 'spd', label: 'SpD', color: '#22c55e' },
              { key: 'spe', label: 'Spe', color: '#ec4899' },
            ] : [];
            const bst = stats ? statList.reduce((s, { key }) => s + (stats[key] || 0), 0) : 0;
            return (
              <div key={p.name} className="search-result search-result-with-stats" onMouseDown={() => handleSelect(p)}>
                {stats && (
                  <div className="search-result-stats">
                    {statList.map(({ key, label, color }) => {
                      const val = stats[key] || 0;
                      return (
                        <div key={key} className="sr-stat" title={`${label} ${val}`}>
                          <span className="sr-stat-label" style={{ color }}>{label}</span>
                          <span className="sr-stat-val">{val}</span>
                        </div>
                      );
                    })}
                    <div className="sr-stat sr-stat-bst" title={`Base Stat Total ${bst}`}>
                      <span className="sr-stat-label">BST</span>
                      <span className="sr-stat-val">{bst}</span>
                    </div>
                  </div>
                )}
                <img src={getSpriteUrl(p.name)} alt="" className="search-result-sprite" onError={e => { e.target.style.display = 'none'; }} />
                <span className="search-result-name">{p.name}</span>
                <div className="search-result-types">
                  {p.types.map(t => <TypeBadge key={t} type={t} />)}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function MoveSearch({ onSelect, currentMoves, pokemonName }) {
  const [query, setQuery] = useState('');
  const [focused, setFocused] = useState(false);
  const inputRef = useRef();

  // Pool is the legal learnset for this Pokemon if we have data; otherwise
  // fall back to all moves (fail-open so unindexed species still work).
  const pool = useMemo(() => {
    if (!pokemonName) return ALL_MOVES;
    const legal = getLegalMoves(pokemonName);
    return legal.length > 0 ? legal : ALL_MOVES;
  }, [pokemonName]);

  const results = useMemo(() => {
    const q = query.toLowerCase();
    const filtered = pool.filter(m =>
      (q.length === 0 || m.toLowerCase().includes(q)) &&
      !currentMoves.includes(m)
    );
    if (!pokemonName) {
      // Without species context we don't have usage to rank on; just alpha.
      return filtered.slice(0, q.length === 0 ? 12 : 8);
    }
    // Rank by doubles usage first, then singles, then alphabetical.
    const scored = filtered.map(m => {
      const u = getMoveUsage(pokemonName, m);
      return { m, d: u.doubles, s: u.singles };
    });
    scored.sort((a, b) => (b.d - a.d) || (b.s - a.s) || a.m.localeCompare(b.m));
    // Show more results when no query — acts as a "top moves" dropdown.
    const limit = q.length === 0 ? 12 : 8;
    return scored.slice(0, limit).map(x => x.m);
  }, [query, pool, currentMoves, pokemonName]);

  const handleSelect = (m) => {
    onSelect(m);
    setQuery('');
    inputRef.current?.focus();
  };

  return (
    <div className="move-search">
      <input
        ref={inputRef}
        type="text"
        placeholder={pokemonName && getLegalMoves(pokemonName).length > 0
          ? `Add a move (${getLegalMoves(pokemonName).length} legal)...`
          : 'Add a move...'}
        value={query}
        onChange={e => setQuery(e.target.value)}
        onFocus={() => setFocused(true)}
        onBlur={() => setTimeout(() => setFocused(false), 150)}
      />
      {focused && results.length > 0 && (
        <div className="search-results move-results">
          {results.map(m => {
            const usage = pokemonName ? getMoveUsage(pokemonName, m) : null;
            return (
              <div key={m} className="search-result move-search-result" onMouseDown={() => handleSelect(m)}>
                <span className="move-result-name" style={moveTypeStyle(m)}>{m}</span>
                {usage && (usage.singles > 0 || usage.doubles > 0) && (
                  <span className="move-usage-pills">
                    {usage.singles > 0 && (
                      <span className="usage-pill usage-singles" title="Singles usage">
                        <span className="usage-pill-label">S</span>
                        <span className="usage-pill-val">{usage.singles.toFixed(1)}%</span>
                      </span>
                    )}
                    {usage.doubles > 0 && (
                      <span className="usage-pill usage-doubles" title="Doubles usage">
                        <span className="usage-pill-label">D</span>
                        <span className="usage-pill-val">{usage.doubles.toFixed(1)}%</span>
                      </span>
                    )}
                  </span>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function AbilitySearch({ onSelect, current, pokemonName }) {
  const [query, setQuery] = useState('');
  const [focused, setFocused] = useState(false);
  const inputRef = useRef();

  // Legal abilities for this Pokemon. Falls back to full list if we don't
  // have data for this species yet.
  const pool = useMemo(() => {
    if (!pokemonName) return ALL_ABILITIES;
    const legal = getAllLegalAbilities(pokemonName);
    return legal.length > 0 ? legal : ALL_ABILITIES;
  }, [pokemonName]);

  const isFiltered = pool !== ALL_ABILITIES;

  const results = useMemo(() => {
    if (query.length < 1) {
      // When filtered, show all legal abilities by default on focus.
      return isFiltered ? pool.slice(0, 6) : [];
    }
    return pool
      .filter(a => a.toLowerCase().includes(query.toLowerCase()))
      .slice(0, 6);
  }, [query, pool, isFiltered]);

  const handleSelect = (a) => {
    onSelect(a);
    setQuery('');
  };

  return (
    <div className="ability-search">
      {current ? (
        <div className="ability-selected">
          <span className="ability-chip">
            <span className="ability-chip-name">
              {current}
              {pokemonName && isHiddenAbility(pokemonName, current) && (
                <span className="ability-ha-tag" title="Hidden Ability">HA</span>
              )}
            </span>
            <span className="ability-chip-desc" title={ABILITIES[current] || ''}>{ABILITIES[current] || ''}</span>
          </span>
          <button className="moveset-remove" onClick={() => onSelect(null)}>&times;</button>
        </div>
      ) : (
        <div className="move-search">
          <input
            ref={inputRef}
            type="text"
            placeholder={isFiltered ? `Search ability (${pool.length} legal)...` : 'Search ability...'}
            value={query}
            onChange={e => setQuery(e.target.value)}
            onFocus={() => setFocused(true)}
            onBlur={() => setTimeout(() => setFocused(false), 150)}
          />
          {focused && results.length > 0 && (
            <div className="search-results move-results">
              {results.map(a => (
                <div key={a} className="search-result" onMouseDown={() => handleSelect(a)}>
                  <span className="ability-result-name">
                    {a}
                    {pokemonName && isHiddenAbility(pokemonName, a) && (
                      <span className="ability-ha-tag" title="Hidden Ability">HA</span>
                    )}
                  </span>
                  <span className="ability-result-desc">{ABILITIES[a]?.slice(0, 50)}{ABILITIES[a]?.length > 50 ? '...' : ''}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

const NATURES = [
  'Adamant','Bold','Brave','Calm','Careful','Gentle','Hasty','Impish',
  'Jolly','Lax','Lonely','Mild','Modest','Naive','Naughty','Quiet',
  'Rash','Relaxed','Sassy','Timid','Hardy','Docile','Serious','Bashful','Quirky',
];

const NATURE_EFFECTS = {
  Adamant: '+Atk / -SpA', Bold: '+Def / -Atk', Brave: '+Atk / -Spe', Calm: '+SpD / -Atk',
  Careful: '+SpD / -SpA', Gentle: '+SpD / -Def', Hasty: '+Spe / -Def', Impish: '+Def / -SpA',
  Jolly: '+Spe / -SpA', Lax: '+Def / -SpD', Lonely: '+Atk / -Def', Mild: '+SpA / -Def',
  Modest: '+SpA / -Atk', Naive: '+Spe / -SpD', Naughty: '+Atk / -SpD', Quiet: '+SpA / -Spe',
  Rash: '+SpA / -SpD', Relaxed: '+Def / -Spe', Sassy: '+SpD / -Spe', Timid: '+Spe / -Atk',
  Hardy: 'Neutral', Docile: 'Neutral', Serious: 'Neutral', Bashful: 'Neutral', Quirky: 'Neutral',
};

function ItemSearch({ onSelect, current }) {
  const [query, setQuery] = useState('');
  const [focused, setFocused] = useState(false);
  const inputRef = useRef();

  const results = query.length >= 1
    ? ALL_ITEMS.filter(i => i.toLowerCase().includes(query.toLowerCase())).slice(0, 6)
    : [];

  return (
    <div className="ability-search">
      {current ? (
        <div className="ability-selected">
          <span className="ability-chip">
            <span className="ability-chip-name">{current}</span>
            <span className="ability-chip-desc">{isMegaStone(current) ? 'Mega Stone' : (ITEMS_INFO[current] || '')}</span>
          </span>
          <button className="moveset-remove" onClick={() => onSelect(null)}>&times;</button>
        </div>
      ) : (
        <div className="move-search">
          <input ref={inputRef} type="text" placeholder="Search item..." value={query}
            onChange={e => setQuery(e.target.value)}
            onFocus={() => setFocused(true)}
            onBlur={() => setTimeout(() => setFocused(false), 150)} />
          {focused && results.length > 0 && (
            <div className="search-results move-results">
              {results.map(item => (
                <div key={item} className="search-result" onMouseDown={() => { onSelect(item); setQuery(''); }}>
                  <span className="ability-result-name">{item}</span>
                  <span className="ability-result-desc">{isMegaStone(item) ? 'Mega Stone' : (ITEMS_INFO[item]?.slice(0, 50) || '')}{ITEMS_INFO[item]?.length > 50 ? '...' : ''}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function NaturePicker({ value, onChange }) {
  const [open, setOpen] = useState(false);
  const ref = useRef();

  useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const effect = NATURE_EFFECTS[value] || 'Neutral';

  // Group natures by boosted stat
  const statOrder = ['Atk', 'Def', 'SpA', 'SpD', 'Spe'];
  const grouped = {};
  for (const n of NATURES) {
    const fx = NATURE_EFFECTS[n];
    if (fx === 'Neutral') continue;
    const boost = fx.split('/')[0].trim().replace('+', '');
    if (!grouped[boost]) grouped[boost] = [];
    grouped[boost].push(n);
  }
  const neutralNatures = NATURES.filter(n => NATURE_EFFECTS[n] === 'Neutral');

  return (
    <div className="nature-picker" ref={ref}>
      <button className="nature-picker-btn" onClick={() => setOpen(!open)}>
        <span className="nature-picker-name">{value}</span>
        <span className="nature-picker-effect">{effect}</span>
      </button>
      {open && (
        <div className="nature-picker-dropdown">
          {statOrder.map(stat => (
            <div key={stat} className="nature-group">
              <div className="nature-group-label">+{stat}</div>
              {(grouped[stat] || []).map(n => (
                <button
                  key={n}
                  className={`nature-picker-option${n === value ? ' active' : ''}`}
                  onClick={() => { onChange(n); setOpen(false); }}
                >
                  <span className="nature-opt-name">{n}</span>
                  <span className="nature-opt-effect">{NATURE_EFFECTS[n]}</span>
                </button>
              ))}
            </div>
          ))}
          <div className="nature-group">
            <div className="nature-group-label">Neutral</div>
            {neutralNatures.map(n => (
              <button
                key={n}
                className={`nature-picker-option${n === value ? ' active' : ''}`}
                onClick={() => { onChange(n); setOpen(false); }}
              >
                <span className="nature-opt-name">{n}</span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

const SP_COLORS = { hp: '#ef4444', atk: '#f59e0b', def: '#f97316', spa: '#3b82f6', spd: '#22c55e', spe: '#ec4899' };

function StatPointEditor({ statPoints, onChange, pokemonName, nature }) {
  const stats = ['hp', 'atk', 'def', 'spa', 'spd', 'spe'];
  const labels = { hp: 'HP', atk: 'Atk', def: 'Def', spa: 'SpA', spd: 'SpD', spe: 'Spe' };
  const total = stats.reduce((sum, s) => sum + (statPoints[s] || 0), 0);
  const remaining = 66 - total;
  const baseStats = pokemonName ? getBaseStats(pokemonName) : null;
  const natureName = nature || '';

  const setSP = (stat, val) => {
    const current = statPoints[stat] || 0;
    const num = Math.max(0, Math.min(32, parseInt(val) || 0));
    // Clamp so total doesn't exceed 66
    const maxAllowed = current + remaining;
    const clamped = Math.min(num, maxAllowed);
    onChange({ ...statPoints, [stat]: clamped });
  };

  const presets = [
    { label: 'Atk/Spe', sp: { hp: 1, atk: 32, def: 0, spa: 0, spd: 0, spe: 32 } },
    { label: 'SpA/Spe', sp: { hp: 1, atk: 0, def: 0, spa: 32, spd: 0, spe: 32 } },
    { label: 'Phys Tank', sp: { hp: 32, atk: 1, def: 32, spa: 0, spd: 0, spe: 0 } },
    { label: 'Spec Tank', sp: { hp: 32, atk: 0, def: 0, spa: 1, spd: 32, spe: 0 } },
  ];

  return (
    <div className="sp-editor">
      <div className="sp-header">
        <span className="sp-title">SP</span>
        <span className={`sp-total${remaining === 0 ? ' full' : ''}`}>{total}/66</span>
        <div className="sp-presets">
          {presets.map(p => (
            <button key={p.label} className="sp-preset" onClick={() => onChange(p.sp)}>{p.label}</button>
          ))}
        </div>
      </div>
      <div className="sp-sliders">
        {stats.map(s => {
          const val = statPoints[s] || 0;
          const max = Math.min(32, val + remaining);
          const baseVal = baseStats ? (baseStats[s] || 0) : null;
          const isHp = s === 'hp';
          const natMod = isHp ? 1.0 : getNatureMod(natureName, s);
          const liveStat = baseVal !== null
            ? calcStat(baseVal, val * 8, natMod, isHp)
            : null;
          const natureClass = natMod > 1 ? ' nature-up' : natMod < 1 ? ' nature-down' : '';
          return (
            <div key={s} className="sp-slider-row">
              <span className="sp-slider-label">{labels[s]}</span>
              {liveStat !== null && (
                <span
                  className={`sp-slider-base${natureClass}`}
                  title={`Base ${baseVal} · Lv50 w/ ${val} SP${natMod !== 1 ? ` · ${natMod > 1 ? '+' : '−'}Nature` : ''}`}
                >
                  {liveStat}
                </span>
              )}
              <input
                type="range"
                className="sp-slider"
                min="0"
                max="32"
                value={val}
                onChange={e => setSP(s, e.target.value)}
                style={{ '--fill': `${(val / 32) * 100}%`, '--color': SP_COLORS[s] }}
              />
              <span className="sp-slider-val">{val}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function PokemonMovesetEditor({ pokemon, onChange }) {
  const moves = pokemon.moves || [];

  const addMove = (move) => {
    if (moves.length < 4) onChange({ ...pokemon, moves: [...moves, move] });
  };
  const removeMove = (move) => {
    onChange({ ...pokemon, moves: moves.filter(m => m !== move) });
  };
  const setAbility = (ability) => onChange({ ...pokemon, ability });
  const setItem = (item) => {
    const updated = { ...pokemon, item };
    // If mega stone, auto-add mega stats
    if (item && isMegaStone(item)) {
      const mega = getMegaStats(pokemon.name);
      if (mega) updated.megaStats = mega;
    } else {
      delete updated.megaStats;
    }
    onChange(updated);
  };
  const setNature = (nature) => onChange({ ...pokemon, nature });
  const setStatPoints = (statPoints) => onChange({ ...pokemon, statPoints });

  return (
    <div className="moveset-editor">
      <div className="moveset-row-2col">
        <div className="moveset-section">
          <div className="moveset-section-label">Ability</div>
          <AbilitySearch current={pokemon.ability || null} onSelect={setAbility} pokemonName={pokemon.name} />
        </div>
        <div className="moveset-section">
          <div className="moveset-section-label">Held Item</div>
          <ItemSearch current={pokemon.item || null} onSelect={setItem} />
        </div>
      </div>
      <div className="moveset-row-2col">
        <div className="moveset-section">
          <div className="moveset-section-label">Nature</div>
          <select className="nature-select" value={pokemon.nature || 'Adamant'} onChange={e => setNature(e.target.value)}>
            {NATURES.map(n => (
              <option key={n} value={n}>{n} ({NATURE_EFFECTS[n]})</option>
            ))}
          </select>
        </div>
        <div className="moveset-section">
          <div className="moveset-section-label">Stat Points</div>
          <StatPointEditor pokemonName={pokemon.name} nature={pokemon.nature} statPoints={pokemon.statPoints || { hp: 0, atk: 0, def: 0, spa: 0, spd: 0, spe: 0 }} onChange={setStatPoints} />
        </div>
      </div>
      <div className="moveset-section">
        <div className="moveset-section-label">Moves</div>
        <div className="moveset-moves">
          {moves.map(m => (
            <div key={m} className="moveset-move" style={moveTypeStyle(m)}>
              <span className="moveset-move-name" style={{ color: 'inherit' }}>{m}</span>
              <button className="moveset-remove" onClick={() => removeMove(m)}>&times;</button>
            </div>
          ))}
          {moves.length === 0 && <span className="moveset-empty">No moves added</span>}
        </div>
        {moves.length < 4 && <MoveSearch onSelect={addMove} currentMoves={moves} pokemonName={pokemon.name} />}
        {moves.length === 4 && <span className="moveset-full">4/4 moves</span>}
      </div>
    </div>
  );
}

function TeamCoverage({ pokemon, megaState = {} }) {
  const weakTo = {};
  const resistsTo = {};
  const immuneTo = {};

  pokemon.forEach(p => {
    const profile = getDefensiveProfile(p.types);
    profile.weaknesses.forEach(w => { weakTo[w.type] = (weakTo[w.type] || 0) + 1; });
    profile.resistances.forEach(r => { resistsTo[r.type] = (resistsTo[r.type] || 0) + 1; });
    profile.immunities.forEach(i => { immuneTo[i] = (immuneTo[i] || 0) + 1; });
  });

  const sortedWeak = Object.entries(weakTo).sort((a, b) => b[1] - a[1]);
  const sortedResist = Object.entries(resistsTo).sort((a, b) => b[1] - a[1]);
  const uncovered = sortedWeak.filter(([type]) => !resistsTo[type] && !immuneTo[type]);

  // Generate team evaluation
  const allTypes = ['Normal','Fire','Water','Electric','Grass','Ice','Fighting','Poison','Ground','Flying','Psychic','Bug','Rock','Ghost','Dragon','Dark','Steel','Fairy'];
  const coveredTypes = allTypes.filter(t => resistsTo[t] || immuneTo[t]);
  const coveragePct = Math.round((coveredTypes.length / allTypes.length) * 100);
  const worstWeak = sortedWeak[0];
  const hasMoveCoverage = pokemon.some(p => (p.moves?.length || 0) >= 3);
  const uniqueTypes = new Set(pokemon.flatMap(p => p.types || []));

  const notes = [];
  if (uncovered.length === 0) notes.push({ type: 'good', text: 'Every type weakness is covered by at least one resist or immunity.' });
  else if (uncovered.length === 1) notes.push({ type: 'warn', text: `${uncovered[0][0]}-type moves have no resist on this team — consider a switch-in for that.` });
  else notes.push({ type: 'bad', text: `${uncovered.length} types have no resist: ${uncovered.map(u => u[0]).join(', ')}. This team has defensive gaps.` });
  if (worstWeak && worstWeak[1] >= 3) notes.push({ type: 'bad', text: `${worstWeak[1]} members are weak to ${worstWeak[0]} — a single spread move could sweep you.` });
  if (uniqueTypes.size <= 3 && pokemon.length >= 4) notes.push({ type: 'warn', text: `Only ${uniqueTypes.size} unique types across ${pokemon.length} Pokemon. Consider more type diversity.` });
  else if (uniqueTypes.size >= 6) notes.push({ type: 'good', text: `Strong type diversity with ${uniqueTypes.size} unique types across the team.` });
  if (Object.keys(immuneTo).length >= 2) notes.push({ type: 'good', text: `${Object.keys(immuneTo).length} type immunities give you safe switch-in options.` });
  if (!hasMoveCoverage && pokemon.length >= 4) notes.push({ type: 'info', text: 'Add moves to your Pokemon for a more detailed offensive coverage analysis.' });

  return (
    <div className="team-coverage">
      {/* Header row: title + score inline */}
      <div className="cov-header">
        <span className="cov-title">Team Analysis</span>
        <span className="cov-pct">{coveragePct}% covered</span>
        {uncovered.length > 0 && (
          <span className="cov-flag">No resist: {uncovered.map(([type]) => (
            <span key={type} className="cov-flag-type" style={{ color: TYPE_COLORS[type] }}>{type}</span>
          ))}</span>
        )}
      </div>

      {/* Notes as compact inline items */}
      <div className="cov-notes">
        {notes.map((n, i) => (
          <div key={i} className={`cov-note cov-note--${n.type}`}>{n.text}</div>
        ))}
      </div>

      {/* Type matchup rows with Pokemon sprites */}
      <div className="cov-rows">
        {allTypes.map(type => {
          const wCount = weakTo[type] || 0;
          const rCount = resistsTo[type] || 0;
          const iCount = immuneTo[type] || 0;
          if (wCount === 0 && rCount === 0 && iCount === 0) return null;
          const isGap = wCount > 0 && rCount === 0 && iCount === 0;

          const weakMons = [];
          const resistMons = [];
          const immuneMons = [];
          pokemon.forEach(p => {
            const profile = getDefensiveProfile(p.types);
            if (profile.immunities.includes(type)) immuneMons.push(p.name);
            else if (profile.weaknesses.some(w => w.type === type)) weakMons.push(p.name);
            else if (profile.resistances.some(r => r.type === type)) resistMons.push(p.name);
          });

          return (
            <div key={type} className={`cov-row${isGap ? ' cov-row--gap' : ''}`}>
              <div className="cov-row-type"><TypeBadge type={type} /></div>
              <div className="cov-row-groups">
                {weakMons.length > 0 && (
                  <div className="cov-row-group cov-row-group--weak">
                    <span className="cov-row-label">weak</span>
                    {weakMons.map(name => {
                      const stone = megaState[name];
                      const displayName = stone ? `Mega ${name}` : name;
                      const spriteUrl = stone ? getMegaSpriteUrl(name, stone) : getSpriteUrl(name);
                      return (
                        <span key={name} className="cov-sprite-wrap" data-name={displayName}>
                          <img src={spriteUrl} alt={displayName} className="cov-row-sprite" onError={e => { e.target.src = getSpriteUrl(name); e.target.onerror = () => { e.target.style.display = 'none'; }; }} />
                        </span>
                      );
                    })}
                  </div>
                )}
                {resistMons.length > 0 && (
                  <div className="cov-row-group cov-row-group--resist">
                    <span className="cov-row-label">resist</span>
                    {resistMons.map(name => {
                      const stone = megaState[name];
                      const displayName = stone ? `Mega ${name}` : name;
                      const spriteUrl = stone ? getMegaSpriteUrl(name, stone) : getSpriteUrl(name);
                      return (
                        <span key={name} className="cov-sprite-wrap" data-name={displayName}>
                          <img src={spriteUrl} alt={displayName} className="cov-row-sprite" onError={e => { e.target.src = getSpriteUrl(name); e.target.onerror = () => { e.target.style.display = 'none'; }; }} />
                        </span>
                      );
                    })}
                  </div>
                )}
                {immuneMons.length > 0 && (
                  <div className="cov-row-group cov-row-group--immune">
                    <span className="cov-row-label">immune</span>
                    {immuneMons.map(name => {
                      const stone = megaState[name];
                      const displayName = stone ? `Mega ${name}` : name;
                      const spriteUrl = stone ? getMegaSpriteUrl(name, stone) : getSpriteUrl(name);
                      return (
                        <span key={name} className="cov-sprite-wrap" data-name={displayName}>
                          <img src={spriteUrl} alt={displayName} className="cov-row-sprite" onError={e => { e.target.src = getSpriteUrl(name); e.target.onerror = () => { e.target.style.display = 'none'; }; }} />
                        </span>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function MyTeams({ onUseInAdvisor }) {
  const { user, loading: authLoading } = useAuth();
  const { teams, loading: teamsLoading, saveTeam: saveTeamToCloud, deleteTeam: deleteTeamFromCloud } = useTeams();
  const [showAuth, setShowAuth] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState('');
  const [editPokemon, setEditPokemon] = useState([]);
  const [editStrategy, setEditStrategy] = useState(() => emptyStrategy());
  const [expandedId, setExpandedId] = useState(null);
  const [expandedSlot, setExpandedSlot] = useState(null);
  // Track mega state per Pokemon: { pokemonName: stoneName | null }
  const [megaState, setMegaState] = useState({});
  const [optimalTeamResult, setOptimalTeamResult] = useState(null);

  const toggleMega = (name, stone) => {
    setMegaState(prev => {
      const current = prev[name];
      // If clicking the same stone, turn off. If different stone, switch to it.
      if (current === stone) return { ...prev, [name]: null };
      return { ...prev, [name]: stone };
    });
  };

  // Match history still lives in localStorage (Firestore migration pending).
  // Reload on each mount and whenever teams change so the stats stay fresh.
  const history = useMemo(() => loadHistory(), [teams]);
  const overallRecord = useMemo(() => getTeamRecord(history), [history]);
  const recordsByTeamName = useMemo(() => {
    const map = {};
    for (const team of teams) {
      map[team.name] = getTeamRecord(history, team.name);
    }
    return map;
  }, [teams, history]);
  const streak = useMemo(() => {
    if (!history.length) return { count: 0, type: null };
    let count = 0;
    let type = null;
    for (let i = history.length - 1; i >= 0; i--) {
      const r = history[i].result;
      const isWin = r === 'win' || r === 'forfeit-win';
      const cur = isWin ? 'W' : 'L';
      if (type === null) { type = cur; count = 1; continue; }
      if (cur === type) count++;
      else break;
    }
    return { count, type };
  }, [history]);

  const startNew = () => {
    setEditingId(Date.now().toString());
    setEditName('');
    setEditPokemon([]);
    setEditStrategy(emptyStrategy());
    setExpandedSlot(null);
  };

  const startEdit = (team) => {
    setEditingId(team.id);
    setEditName(team.name);
    setEditPokemon(team.pokemon.map(p => ({ ...p, moves: p.moves || [] })));
    setEditStrategy(normalizeStrategy(team.strategy));
    setExpandedSlot(null);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditName('');
    setEditPokemon([]);
    setEditStrategy(emptyStrategy());
    setExpandedSlot(null);
  };

  const addPokemon = (p) => {
    if (editPokemon.length < 6) {
      setEditPokemon([...editPokemon, p]);
      setExpandedSlot(editPokemon.length); // auto-expand new one for move editing
    }
  };

  const removePokemon = (name) => {
    setEditPokemon(editPokemon.filter(p => p.name !== name));
    setExpandedSlot(null);
  };

  const updatePokemon = (index, updated) => {
    const arr = [...editPokemon];
    arr[index] = updated;
    setEditPokemon(arr);
  };

  const movePokemon = (index, direction) => {
    const arr = [...editPokemon];
    const target = index + direction;
    if (target < 0 || target >= arr.length) return;
    [arr[index], arr[target]] = [arr[target], arr[index]];
    setEditPokemon(arr);
    if (expandedSlot === index) setExpandedSlot(target);
    else if (expandedSlot === target) setExpandedSlot(index);
  };

  const saveTeam = async () => {
    if (!editName.trim() || editPokemon.length === 0) return;
    try {
      await saveTeamToCloud({
        id: editingId,
        name: editName.trim(),
        pokemon: editPokemon,
        strategy: normalizeStrategy(editStrategy),
      });
      track('team_saved', {
        is_new: !editingId,
        size: editPokemon.length,
      });
      cancelEdit();
    } catch (err) {
      console.error('Failed to save team:', err);
      alert('Failed to save team. Please try again.');
    }
  };

  // Persist a strategy patch immediately without leaving the editor. Used by
  // StrategyEditor's "Import from preset" so the imported strategy actually
  // hits Firestore — otherwise the editor state changes but the team in the
  // cloud stays empty until the user remembers to click Save Team.
  // Throws if the team has no name or no pokemon yet (caller should prompt
  // the user to fill those in before importing).
  const saveStrategyImmediate = async (strategy) => {
    if (!editName.trim()) throw new Error('Team needs a name before strategy can be saved.');
    if (editPokemon.length === 0) throw new Error('Add at least one Pokémon before importing a strategy.');
    const normalized = normalizeStrategy(strategy);
    // eslint-disable-next-line no-console
    console.log('[strategy:save] writing to Firestore', {
      editingId,
      name: editName.trim(),
      pokemonCount: editPokemon.length,
      bringPriorsKeys: Object.keys(normalized.bringPriors),
      triggers: normalized.triggers.length,
      defaultLead: normalized.leadRules.defaultLead,
    });
    await saveTeamToCloud({
      id: editingId,
      name: editName.trim(),
      pokemon: editPokemon,
      strategy: normalized,
    });
    // eslint-disable-next-line no-console
    console.log('[strategy:save] write complete');
  };

  const deleteTeam = async (id) => {
    try {
      await deleteTeamFromCloud(id);
      if (expandedId === id) setExpandedId(null);
    } catch (err) {
      console.error('Failed to delete team:', err);
    }
  };

  const duplicateTeam = async (team) => {
    try {
      await saveTeamToCloud({
        id: Date.now().toString(),
        name: team.name + ' (copy)',
        pokemon: team.pokemon.map(p => ({ ...p, moves: [...(p.moves || [])] })),
      });
    } catch (err) {
      console.error('Failed to duplicate team:', err);
    }
  };

  const handleUseInAdvisor = (team) => {
    // eslint-disable-next-line no-console
    console.log('[myteams:useInAdvisor] passing team', {
      id: team.id,
      name: team.name,
      hasStrategy: !!team.strategy,
      strategyKeys: team.strategy && typeof team.strategy === 'object' ? Object.keys(team.strategy) : null,
      strategy: team.strategy,
    });
    const advisorTeam = {
      id: team.id,
      name: team.name,
      archetype: 'Custom',
      pokemon: team.pokemon.map(p => ({
        ...p,
        moves: p.moves || [],
        role: '',
      })),
      // Carry the structured strategy through so the advisor doesn't have to
      // re-fetch it via the savedTeams subscription (which can be stale right
      // after a save). If the strategy was just imported, this is the most
      // reliable way to get it to the analyzer.
      strategy: team.strategy || null,
    };
    onUseInAdvisor(advisorTeam);
  };

  if (authLoading) {
    return (
      <div className="my-teams">
        <div className="my-teams-header"><h2>My Teams</h2></div>
      </div>
    );
  }

  return (
    <div className="my-teams">
      <div className="my-teams-header">
        <h2>My Teams</h2>
        <p className="subtitle">
          {user
            ? 'Build and save your own teams with movesets. Your teams sync to your account.'
            : 'Build teams with movesets, stats, and items — saved right in this browser.'}
        </p>
        {editingId === null && (
          <div className="my-teams-actions">
            <button className="create-team-btn" onClick={startNew}>+ New Team</button>
            <button
              className="optimal-team-btn"
              onClick={() => {
                track('optimal_team_click', { matches: history.length });
                setOptimalTeamResult(computeOptimalTeam(history));
              }}
              title="Build a team that counters this season's most-used Pokemon"
            >
              ✨ Suggest Optimal Team
            </button>
          </div>
        )}
      </div>

      {!user && (
        <div className="mt-anon-banner" role="note">
          <div className="mt-anon-banner-icon" aria-hidden="true">☁️</div>
          <div className="mt-anon-banner-copy">
            <strong>You're using PK Champs Coach as a guest.</strong>
            <span>
              {teams.length > 0
                ? `Your ${teams.length} team${teams.length === 1 ? '' : 's'} ${teams.length === 1 ? 'is' : 'are'} saved on this device only. Create a free account to sync across devices and back up your match history.`
                : 'Teams you build here are saved on this device only. Create a free account to sync across devices and save match history.'}
            </span>
          </div>
          <button
            className="mt-anon-banner-btn"
            onClick={() => { track('auth_prompt_click', { source: 'myteams_banner' }); setShowAuth(true); }}
          >
            Create account
          </button>
        </div>
      )}

      {overallRecord.total > 0 && (
        <div className="mt-stats">
          <div className="mt-stat">
            <span className="mt-stat-num">{overallRecord.totalWins}</span>
            <span className="mt-stat-label">Wins</span>
          </div>
          <div className="mt-stat">
            <span className="mt-stat-num">{overallRecord.losses + overallRecord.forfeitLosses}</span>
            <span className="mt-stat-label">Losses</span>
          </div>
          <div className="mt-stat">
            <span className="mt-stat-num">{overallRecord.winRate}<span className="mt-stat-unit">%</span></span>
            <span className="mt-stat-label">Win Rate</span>
          </div>
          <div className="mt-stat">
            <span className={`mt-stat-num mt-streak mt-streak-${streak.type === 'W' ? 'win' : 'loss'}`}>
              {streak.type === 'W' ? '+' : '−'}{streak.count}
              {streak.count >= 3 && streak.type === 'W' && <span className="mt-stat-flame" aria-hidden="true">🔥</span>}
            </span>
            <span className="mt-stat-label">{streak.type === 'W' ? 'Win' : 'Loss'} Streak</span>
          </div>
          <div className="mt-stat mt-stat-total">
            <span className="mt-stat-num">{overallRecord.total}</span>
            <span className="mt-stat-label">Matches</span>
          </div>
        </div>
      )}

      {/* Saved teams grid (always visible) */}
      {teamsLoading ? (
        <div className="no-teams"><p>Loading your teams…</p></div>
      ) : teams.length === 0 && editingId === null ? (
        <div className="no-teams">
          <p>No saved teams yet. Click "New Team" to get started.</p>
        </div>
      ) : (
      <div className="team-grid">
          {teams.map(team => {
            const isExpanded = expandedId === team.id || editingId === team.id;
            const record = recordsByTeamName[team.name];
            const hasRecord = record && record.total > 0;
            return (
            <div
              key={team.id}
              className={`team-card${isExpanded ? ' team-card-expanded' : ''}`}
              onClick={() => { if (!isExpanded) setExpandedId(team.id); }}
            >
              <div className="team-card-header">
                <span className="format-tag champions">Custom</span>
                <span className="saved-team-count">{team.pokemon.length} Pokemon</span>
                {hasRecord && (
                  <span className={`team-record-chip${record.winRate >= 60 ? ' hot' : record.winRate < 40 ? ' cold' : ''}`}>
                    <span className="team-record-wl">
                      <strong>{record.totalWins}</strong>W &middot; <strong>{record.losses + record.forfeitLosses}</strong>L
                    </span>
                    <span className="team-record-pct">{record.winRate}%</span>
                  </span>
                )}
              </div>
              <h3>{team.name}</h3>
              {!isExpanded && (
                <div className="team-pokemon-preview">
                  {team.pokemon.map(p => (
                    <div key={p.name} className="pokemon-mini">
                      <img src={getSpriteUrl(p.name)} alt={p.name} className="pokemon-mini-sprite" onError={e => { e.target.style.display = 'none'; }} />
                      <span className="pokemon-mini-name">{p.name}</span>
                      <div className="pokemon-mini-types">
                        {p.types.map(t => <TypeBadge key={t} type={t} />)}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {isExpanded && editingId !== team.id && (
                <div className="saved-team-expanded" onClick={e => e.stopPropagation()}>
                  <div className="saved-team-collapse-row">
                    <button className="saved-team-collapse" onClick={() => setExpandedId(null)}>
                      &larr; Back to teams
                    </button>
                  </div>
                  <div className="saved-team-actions">
                    <button className="action-btn edit" onClick={() => startEdit(team)}>Edit</button>
                    <button className="action-btn duplicate" onClick={() => duplicateTeam(team)}>Duplicate</button>
                    <button className="action-btn use" onClick={() => handleUseInAdvisor(team)}>
                      Use in Battle Advisor
                    </button>
                    <button className="action-btn delete" onClick={() => deleteTeam(team.id)}>Delete</button>
                  </div>
                  <div className="saved-team-grid">
                    {team.pokemon.map((p, idx) => {
                      return (() => {
                        const megaStones = getMegaStones(p.name);
                        const hasMega = megaStones.length > 0;
                        const activeMegaStone = megaState[p.name] || null;
                        const isMega = !!activeMegaStone;

                        // Resolve displayed stats/types/ability based on mega state
                        const baseStats = p.baseStats || getBaseStats(p.name);
                        const displayStats = isMega ? (getMegaStatsByStone(p.name, activeMegaStone) || baseStats) : baseStats;
                        const megaTypes = isMega ? getMegaTypes(p.name, activeMegaStone) : null;
                        const displayTypes = megaTypes || p.types;
                        const displayName = isMega ? `Mega ${p.name}${megaStones.length > 1 ? (activeMegaStone.includes('X') ? ' X' : ' Y') : ''}` : p.name;
                        const displayAbility = isMega ? (getMegaAbility(p.name, activeMegaStone) || p.ability) : p.ability;

                        return (
                      <div key={p.name} className={`saved-pokemon-card${isMega ? ' mega-active' : ''}`} style={{ animationDelay: `${idx * 0.05}s` }}>
                        <div className="saved-pokemon-card-top">
                          <img src={isMega ? getMegaSpriteUrl(p.name, activeMegaStone) : getSpriteUrl(p.name)} alt="" className="saved-pokemon-card-sprite" onError={e => { e.target.src = getSpriteUrl(p.name); e.target.onerror = () => { e.target.style.display = 'none'; }; }} />
                          <div className="saved-pokemon-card-identity">
                            <span className="saved-pokemon-card-name">{displayName}</span>
                            <div className="saved-pokemon-card-types">
                              {displayTypes.map(t => <TypeBadge key={t} type={t} />)}
                            </div>
                          </div>
                        </div>

                        {/* Mega toggle — always rendered for consistent height */}
                        <div className="mega-toggle-row">
                          {hasMega && megaStones.map(({ stone }) => {
                              const label = megaStones.length > 1
                                ? (stone.includes('X') ? 'Mega X' : 'Mega Y')
                                : 'Mega';
                              const isActive = activeMegaStone === stone;
                              return (
                                <button
                                  key={stone}
                                  className={`mega-toggle-btn${isActive ? ' active' : ''}`}
                                  onClick={() => toggleMega(p.name, stone)}
                                >
                                  {isActive ? '✦ ' : ''}{label}
                                </button>
                              );
                            })}
                          {!hasMega && <span className="mega-toggle-spacer">&nbsp;</span>}
                        </div>

                        <div className="saved-pokemon-card-meta">
                          {displayAbility && <span className="saved-pokemon-meta-item ability" data-tooltip={ABILITIES[displayAbility] || ''}>{displayAbility}</span>}
                          {p.item && <span className="saved-pokemon-meta-item item" data-tooltip={ITEMS_INFO[p.item] || ''}><img src={getItemSpriteUrl(p.item)} alt="" className="item-sprite" onError={e => handleItemIconError(e, p.item)} />{p.item}</span>}
                          {p.nature && <span className="saved-pokemon-meta-item nature">{p.nature}</span>}
                        </div>
                        {/* Stat bars */}
                        {displayStats && (() => {
                          const sp = p.statPoints || {};
                          const evs = p.evs || {};
                          const nature = p.nature || '';
                          const natureMap = {
                            Adamant: { up: 'atk', down: 'spa' }, Bold: { up: 'def', down: 'atk' },
                            Brave: { up: 'atk', down: 'spe' }, Calm: { up: 'spd', down: 'atk' },
                            Careful: { up: 'spd', down: 'spa' }, Gentle: { up: 'spd', down: 'def' },
                            Hasty: { up: 'spe', down: 'def' }, Impish: { up: 'def', down: 'spa' },
                            Jolly: { up: 'spe', down: 'spa' }, Lax: { up: 'def', down: 'spd' },
                            Lonely: { up: 'atk', down: 'def' }, Mild: { up: 'spa', down: 'def' },
                            Modest: { up: 'spa', down: 'atk' }, Naive: { up: 'spe', down: 'spd' },
                            Naughty: { up: 'atk', down: 'spd' }, Quiet: { up: 'spa', down: 'spe' },
                            Rash: { up: 'spa', down: 'spd' }, Relaxed: { up: 'def', down: 'spe' },
                            Sassy: { up: 'spd', down: 'spe' }, Timid: { up: 'spe', down: 'atk' },
                          };
                          const nm = natureMap[nature] || {};
                          const statList = [
                            { key: 'hp', label: 'HP', color: '#ef4444' },
                            { key: 'atk', label: 'Atk', color: '#f59e0b' },
                            { key: 'def', label: 'Def', color: '#f97316' },
                            { key: 'spa', label: 'SpA', color: '#3b82f6' },
                            { key: 'spd', label: 'SpD', color: '#22c55e' },
                            { key: 'spe', label: 'Spe', color: '#ec4899' },
                          ];
                          const bst = statList.reduce((s, { key }) => s + (displayStats[key] || 0), 0);
                          return (
                            <div className="pkmn-stats">
                              {statList.map(({ key, label, color }) => {
                                const val = displayStats[key] || 0;
                                const baseVal = baseStats ? (baseStats[key] || 0) : val;
                                const diff = isMega ? val - baseVal : 0;
                                const spVal = sp[key] || (evs[key] ? Math.round(evs[key] / 8) : 0);
                                const isUp = nm.up === key;
                                const isDown = nm.down === key;
                                return (
                                  <div key={key} className="pkmn-stat">
                                    <span className={`pkmn-stat-label${isUp ? ' pkmn-stat-up' : ''}${isDown ? ' pkmn-stat-down' : ''}`}>
                                      {isUp && <span className="pkmn-nature-arrow up">▲</span>}
                                      {isDown && <span className="pkmn-nature-arrow down">▼</span>}
                                      {label}
                                    </span>
                                    <span className="pkmn-stat-base">{val}{diff !== 0 && <span className={`pkmn-stat-diff${diff > 0 ? ' pos' : ' neg'}`}>{diff > 0 ? '+' : ''}{diff}</span>}</span>
                                    <div className="pkmn-stat-track">
                                      <div className="pkmn-stat-fill" style={{ width: `${Math.min(val / 200 * 100, 100)}%`, background: color }} />
                                    </div>
                                    <span className="pkmn-stat-ev">{spVal > 0 ? spVal : ''}</span>
                                  </div>
                                );
                              })}
                              <div className="pkmn-stat-bst">BST {bst}</div>
                            </div>
                          );
                        })()}
                        <div className="saved-pokemon-card-moves">
                          {(p.moves?.length || 0) > 0 ? (
                            p.moves.map(m => {
                              const md = MOVE_DATA[m];
                              const mt = MOVE_TYPES[m] || STATUS_MOVE_TYPES[m] || null;
                              const power = md?.power;
                              const cat = md?.cat;
                              const isStatus = !mt || cat === 'status' || (!md && mt === null);
                              const catIcon = cat === 'physical' ? '⚔' : cat === 'special' ? '◎' : '';
                              const tipParts = [
                                mt ? `${mt}-type` : 'Status',
                                cat || (isStatus ? 'status' : null),
                                power && `${power} BP`,
                                md?.spread && 'spread',
                                md?.priority && `+${md.priority} priority`,
                              ].filter(Boolean);
                              // Add description for status/known moves
                              const desc = MOVE_DESCRIPTIONS[m];
                              if (desc) tipParts.push(desc);
                              const tooltip = tipParts.length > 0 ? tipParts.join(' · ') : m;
                              return (
                                <span key={m} className="saved-pokemon-move" style={moveTypeStyle(m)} data-tooltip={tooltip}>
                                  <img src={getTypeIconUrl(mt || 'Normal')} alt={mt || 'Status'} className="move-type-icon" />
                                  {m}{power ? <span className="move-power">{catIcon}{power}</span> : isStatus ? <span className="move-power">◈</span> : null}
                                </span>
                              );
                            })
                          ) : (
                            <span className="moveset-empty">No moves</span>
                          )}
                        </div>
                      </div>
                        ); })();
                    })}
                  </div>
                  <TeamCoverage pokemon={team.pokemon.map(p => {
                    const stone = megaState[p.name];
                    if (!stone) return p;
                    const megaTypes = getMegaTypes(p.name, stone);
                    return megaTypes ? { ...p, types: megaTypes, _megaStone: stone } : { ...p, _megaStone: stone };
                  })} megaState={megaState} />
                </div>
              )}

              {/* Inline edit mode for this team */}
              {editingId === team.id && (
                <div className="saved-team-expanded editing-mode">
                  <div className="inline-edit-header">
                    <input
                      className="team-name-input"
                      type="text"
                      placeholder="Team name..."
                      value={editName}
                      onChange={e => setEditName(e.target.value)}
                      autoFocus
                    />
                    <div className="inline-edit-actions">
                      <button className="cancel-btn" onClick={cancelEdit}>Cancel</button>
                      <button className="save-btn" onClick={saveTeam} disabled={!editName.trim() || editPokemon.length === 0}>
                        Save ({editPokemon.length}/6)
                      </button>
                    </div>
                  </div>

                  <div className="saved-team-grid">
                    {editPokemon.map((p, idx) => (
                      <div key={p.name} className={`saved-pokemon-card editing ${expandedSlot === idx ? 'slot-expanded' : ''}`} style={{ animationDelay: `${idx * 0.05}s` }}>
                        <div className="edit-card-toolbar">
                          <button className="move-btn" onClick={() => movePokemon(idx, -1)} disabled={idx === 0} title="Move left">&larr;</button>
                          <button className="move-btn" onClick={() => movePokemon(idx, 1)} disabled={idx === editPokemon.length - 1} title="Move right">&rarr;</button>
                          <button className="remove-btn" onClick={() => removePokemon(p.name)} title="Remove">&times;</button>
                        </div>
                        <div className="saved-pokemon-card-top">
                          <img src={getSpriteUrl(p.name)} alt="" className="saved-pokemon-card-sprite" onError={e => { e.target.style.display = 'none'; }} />
                          <div className="saved-pokemon-card-identity">
                            <span className="saved-pokemon-card-name">{p.name}</span>
                            <div className="saved-pokemon-card-types">
                              {p.types.map(t => <TypeBadge key={t} type={t} />)}
                            </div>
                          </div>
                        </div>
                        <div className="edit-inline-fields">
                          <div className="edit-inline-row">
                            <AbilitySearch current={p.ability} onSelect={a => updatePokemon(idx, { ...p, ability: a })} pokemonName={p.name} />
                          </div>
                          <div className="edit-inline-row">
                            <ItemSearch current={p.item} onSelect={item => {
                              const updated = { ...p, item };
                              if (item && isMegaStone(item)) { const mega = getMegaStats(p.name); if (mega) updated.megaStats = mega; } else { delete updated.megaStats; }
                              updatePokemon(idx, updated);
                            }} />
                          </div>
                          <div className="edit-inline-row">
                            <NaturePicker value={p.nature || 'Adamant'} onChange={n => updatePokemon(idx, { ...p, nature: n })} />
                          </div>
                        </div>
                        <StatPointEditor pokemonName={p.name} nature={p.nature} statPoints={p.statPoints || { hp: 0, atk: 0, def: 0, spa: 0, spd: 0, spe: 0 }} onChange={sp => updatePokemon(idx, { ...p, statPoints: sp })} />
                        <div className="edit-inline-moves">
                          <div className="edit-moves-list">
                            {(p.moves || []).map(m => (
                              <span key={m} className="saved-pokemon-move" style={moveTypeStyle(m)}>
                                <img src={getTypeIconUrl(MOVE_TYPES[m] || STATUS_MOVE_TYPES[m] || 'Normal')} alt="" className="move-type-icon" />
                                {m}
                                <button className="move-remove-x" onClick={() => updatePokemon(idx, { ...p, moves: p.moves.filter(mv => mv !== m) })}>&times;</button>
                              </span>
                            ))}
                          </div>
                          {(p.moves?.length || 0) < 4 && (
                            <MoveSearch onSelect={mv => updatePokemon(idx, { ...p, moves: [...(p.moves || []), mv] })} currentMoves={p.moves || []} pokemonName={p.name} />
                          )}
                        </div>
                      </div>
                    ))}
                    {editPokemon.length < 6 && (
                      <div className="saved-pokemon-card add-slot">
                        <PokemonSearch onSelect={addPokemon} exclude={editPokemon} />
                      </div>
                    )}
                  </div>

                  {editPokemon.length > 0 && <TeamCoverage pokemon={editPokemon} />}

                  <StrategyEditor
                    teamPokemon={editPokemon}
                    strategy={editStrategy}
                    onChange={setEditStrategy}
                    onSaveImmediate={saveStrategyImmediate}
                  />
                </div>
              )}
            </div>
            );
          })}

          {/* New team creation (inline at bottom) */}
          {editingId !== null && !teams.find(t => t.id === editingId) && (
            <div className="team-card team-card-expanded editing-new" onClick={e => e.stopPropagation()}>
              <div className="saved-team-expanded">
                <div className="inline-edit-header">
                  <input
                    className="team-name-input"
                    type="text"
                    placeholder="Team name..."
                    value={editName}
                    onChange={e => setEditName(e.target.value)}
                    autoFocus
                  />
                  <div className="inline-edit-actions">
                    <button className="cancel-btn" onClick={cancelEdit}>Cancel</button>
                    <button className="save-btn" onClick={saveTeam} disabled={!editName.trim() || editPokemon.length === 0}>
                      Save ({editPokemon.length}/6)
                    </button>
                  </div>
                </div>

                <div className="saved-team-grid">
                  {editPokemon.map((p, idx) => (
                    <div key={p.name} className={`saved-pokemon-card editing ${expandedSlot === idx ? 'slot-expanded' : ''}`} style={{ animationDelay: `${idx * 0.05}s` }}>
                      <div className="edit-card-toolbar">
                        <button className="move-btn" onClick={() => movePokemon(idx, -1)} disabled={idx === 0} title="Move left">&larr;</button>
                        <button className="move-btn" onClick={() => movePokemon(idx, 1)} disabled={idx === editPokemon.length - 1} title="Move right">&rarr;</button>
                        <button className="remove-btn" onClick={() => removePokemon(p.name)} title="Remove">&times;</button>
                      </div>
                      <div className="saved-pokemon-card-top">
                        <img src={getSpriteUrl(p.name)} alt="" className="saved-pokemon-card-sprite" onError={e => { e.target.style.display = 'none'; }} />
                        <div className="saved-pokemon-card-identity">
                          <span className="saved-pokemon-card-name">{p.name}</span>
                          <div className="saved-pokemon-card-types">
                            {p.types.map(t => <TypeBadge key={t} type={t} />)}
                          </div>
                        </div>
                      </div>
                      {/* Inline editable fields */}
                      <div className="edit-inline-fields">
                        <div className="edit-inline-row">
                          <AbilitySearch current={p.ability} onSelect={a => updatePokemon(idx, { ...p, ability: a })} pokemonName={p.name} />
                        </div>
                        <div className="edit-inline-row">
                          <ItemSearch current={p.item} onSelect={item => {
                            const updated = { ...p, item };
                            if (item && isMegaStone(item)) { const mega = getMegaStats(p.name); if (mega) updated.megaStats = mega; } else { delete updated.megaStats; }
                            updatePokemon(idx, updated);
                          }} />
                        </div>
                        <div className="edit-inline-row">
                          <NaturePicker value={p.nature || 'Adamant'} onChange={n => updatePokemon(idx, { ...p, nature: n })} />
                        </div>
                      </div>
                      {/* Inline stat points */}
                      <StatPointEditor pokemonName={p.name} nature={p.nature} statPoints={p.statPoints || { hp: 0, atk: 0, def: 0, spa: 0, spd: 0, spe: 0 }} onChange={sp => updatePokemon(idx, { ...p, statPoints: sp })} />
                      {/* Inline moves */}
                      <div className="edit-inline-moves">
                        <div className="edit-moves-list">
                          {(p.moves || []).map(m => (
                            <span key={m} className="saved-pokemon-move" style={moveTypeStyle(m)}>
                              <img src={getTypeIconUrl(MOVE_TYPES[m] || STATUS_MOVE_TYPES[m] || 'Normal')} alt="" className="move-type-icon" />
                              {m}
                              <button className="move-remove-x" onClick={() => updatePokemon(idx, { ...p, moves: p.moves.filter(mv => mv !== m) })}>&times;</button>
                            </span>
                          ))}
                        </div>
                        {(p.moves?.length || 0) < 4 && (
                          <MoveSearch onSelect={mv => updatePokemon(idx, { ...p, moves: [...(p.moves || []), mv] })} currentMoves={p.moves || []} pokemonName={p.name} />
                        )}
                      </div>
                    </div>
                  ))}
                  {editPokemon.length < 6 && (
                    <div className="saved-pokemon-card add-slot">
                      <PokemonSearch onSelect={addPokemon} exclude={editPokemon} />
                    </div>
                  )}
                </div>

                {editPokemon.length > 0 && <TeamCoverage pokemon={editPokemon} />}

                <StrategyEditor
                  teamPokemon={editPokemon}
                  strategy={editStrategy}
                  onChange={setEditStrategy}
                  onSaveImmediate={saveStrategyImmediate}
                />
              </div>
            </div>
          )}
        </div>
      )}

      {optimalTeamResult && (
        <OptimalTeamModal
          result={optimalTeamResult}
          onClose={() => setOptimalTeamResult(null)}
          onUseAsTeam={() => {
            const poke = optimalTeamResult.team.map(p => {
              const meta = ALL_POKEMON.find(x => x.name === p.name);
              return meta ? { ...meta, moves: [] } : null;
            }).filter(Boolean);
            setEditingId(Date.now().toString());
            setEditName('Optimal Team');
            setEditPokemon(poke);
            setEditStrategy(emptyStrategy());
            setExpandedSlot(null);
            setOptimalTeamResult(null);
          }}
        />
      )}

      {showAuth && (
        <AuthModal
          onClose={() => setShowAuth(false)}
          initialMode="signup"
          message="Create a free account to back up your teams and match history across devices."
        />
      )}
    </div>
  );
}

function OptimalTeamModal({ result, onClose, onUseAsTeam }) {
  const { team, topThreats, coveredThreats, totalThreats, lowDataWarning, totalMatches } = result;

  return (
    <div className="optimal-modal-backdrop" onClick={onClose}>
      <div className="optimal-modal" onClick={e => e.stopPropagation()}>
        <button className="optimal-modal-close" onClick={onClose} aria-label="Close">×</button>
        <div className="optimal-modal-header">
          <h2>Your Optimal Team</h2>
          <p className="optimal-modal-sub">
            Built to counter the <strong>top {totalThreats}</strong> most-used Pokemon this season
            {totalThreats > 0 && <> &middot; Answers <strong>{coveredThreats}/{totalThreats}</strong></>}
            {totalMatches > 0 && <> &middot; Tuned by your <strong>{totalMatches}</strong> match{totalMatches === 1 ? '' : 'es'}</>}
          </p>
          {lowDataWarning && (
            <p className="optimal-modal-warning">
              ℹ Recommendations are based on the current Reg M-A meta usage rankings. Log more matches to layer in your personal win-rate data.
            </p>
          )}
        </div>

        <div className="optimal-team-grid">
          {team.map((p, idx) => (
            <div key={p.name + idx} className="optimal-team-card">
              <div className="optimal-team-card-sprite-wrap">
                <img
                  src={getSpriteUrl(p.name)}
                  alt={p.name}
                  className="optimal-team-card-sprite"
                  onError={e => { e.target.style.display = 'none'; }}
                />
              </div>
              <div className="optimal-team-card-body">
                <div className="optimal-team-card-name">
                  {p.name}
                  <span className={`optimal-source-badge ${p.timesBrought > 0 ? 'src-history' : 'src-roster'}`}>
                    {p.timesBrought > 0 ? 'From your matches' : 'New roster pick'}
                  </span>
                </div>
                <div className="optimal-team-card-types">
                  {(p.types || []).map(t => <TypeBadge key={t} type={t} />)}
                </div>
                {p.timesBrought > 0 && (
                  <div className="optimal-team-card-winrate">
                    <span
                      className="optimal-winrate-num"
                      style={{ color: p.winRate >= 60 ? '#4ade80' : p.winRate >= 45 ? '#fbbf24' : '#f87171' }}
                    >
                      {p.winRate}%
                    </span>
                    <span className="optimal-winrate-label">
                      {p.wins}-{p.timesBrought - p.wins} in {p.timesBrought} games
                    </span>
                  </div>
                )}
                {p.reasons && p.reasons.length > 0 && (
                  <ul className="optimal-team-card-reasons">
                    {p.reasons.map((r, i) => <li key={i}>{r}</li>)}
                  </ul>
                )}
              </div>
            </div>
          ))}
        </div>

        {topThreats && topThreats.length > 0 && (
          <div className="optimal-threats-section">
            <h3>Meta Threats This Team Targets</h3>
            <div className="optimal-threats-list">
              {topThreats.map(t => (
                <div key={t.name} className="optimal-threat-chip">
                  <span className="optimal-threat-rank">#{t.rank}</span>
                  <img
                    src={getSpriteUrl(t.name)}
                    alt=""
                    className="optimal-threat-sprite"
                    onError={e => { e.target.style.display = 'none'; }}
                  />
                  <div className="optimal-threat-info">
                    <span className="optimal-threat-name">{t.name}</span>
                    <span className="optimal-threat-rate">
                      {t.winRate !== null && t.encounters > 0
                        ? `You: ${t.winRate}% in ${t.encounters} game${t.encounters === 1 ? '' : 's'}`
                        : 'Reg M-A meta usage'}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="optimal-modal-actions">
          <button className="optimal-modal-secondary" onClick={onClose}>Close</button>
          <button className="optimal-modal-primary" onClick={onUseAsTeam}>
            Use as New Team
          </button>
        </div>
      </div>
    </div>
  );
}

export default MyTeams;
